/**
 * NOS-133 M14: Registrations table with cancel / promote and CSV export.
 */

import { Link } from "react-router-dom"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import type { RegistrationWithTicket } from "@/hooks/useEventRegistrations"

export interface RegistrationsListProps {
  registrations: RegistrationWithTicket[]
  onCancel: (id: string) => void
  onPromote: (id: string) => void
  onExportCsv: () => void
}

function statusLabel(status: string): string {
  if (status === "confirmed") return "Confirmed"
  if (status === "waitlisted") return "Waitlisted"
  if (status === "cancelled") return "Cancelled"
  return status
}

function statusVariant(
  status: string
): "default" | "secondary" | "destructive" | "outline" {
  if (status === "confirmed") return "default"
  if (status === "waitlisted") return "secondary"
  if (status === "cancelled") return "outline"
  return "outline"
}

export function RegistrationsList({
  registrations,
  onCancel,
  onPromote,
  onExportCsv,
}: RegistrationsListProps) {
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button type="button" variant="outline" size="sm" onClick={onExportCsv}>
          Download CSV
        </Button>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Ticket type</TableHead>
              <TableHead className="text-right">Headcount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Registered</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {registrations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground">
                  No registrations yet.
                </TableCell>
              </TableRow>
            ) : (
              registrations.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">
                    {r.contact_id ? (
                      <Link
                        to={`/dashboard/contacts/${r.contact_id}`}
                        className="text-primary hover:underline"
                      >
                        {r.name}
                      </Link>
                    ) : (
                      r.name
                    )}
                  </TableCell>
                  <TableCell>{r.email}</TableCell>
                  <TableCell>{r.event_ticket_types?.name ?? "—"}</TableCell>
                  <TableCell className="text-right">{r.headcount}</TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(r.status)}>{statusLabel(r.status)}</Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {new Date(r.registered_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    {r.status === "confirmed" && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => onCancel(r.id)}
                      >
                        Cancel
                      </Button>
                    )}
                    {r.status === "waitlisted" && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => onPromote(r.id)}>
                        Promote
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
